import moment from "moment";
import { currencyOptionsWithSpecials } from "./currencies";

/**
 * Formats a date for table display. Returns null if value is missing or not parseable
 * (avoids showing "Invalid date" / moment artifacts).
 */
export function formatSafeTableDate(value, format = "DD/MM/YYYY") {
  if (value === null || value === undefined || value === "") return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  return m.format(format);
}

/** Fecha calendario guardada en UTC (p. ej. operaciones de estado de cuenta). */
export function formatUtcCalendarDate(value, format = "DD/MM/YYYY") {
  if (value === null || value === undefined || value === "") return null;
  const m = moment.utc(value);
  if (!m.isValid()) return null;
  return m.format(format);
}

export function toMoney(amount) {
  return `$ ${amount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
export function toMoneyFinance(amount) {
  if (amount >= 0) return `$ ${amount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  else return `$ (${amount * -1})`.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function formatMoneyInMillions(amount, currency = '', includePrefix = true) {
  const invalidAmountInputs = [undefined, null, "",];

  if (typeof amount === 'string') {
    amount = Number(amount)
  }

  if (currency === '' && process.env.REACT_APP_COUNTRY === "CHL") currency = 'CLP'
  if (currency === '' && process.env.REACT_APP_COUNTRY === "COL") currency = 'COP'
  
  if (invalidAmountInputs.includes(amount)) return "";

  const currencyOptions = currencyOptionsWithSpecials.find(
    (option) => option.key === currency
  );

  if (!currencyOptions) return "";

  // Convert to millions
  const amountInMillions = amount / 1000000;
  
  // Format with 1 decimal place
  const formattedAmount = amountInMillions.toFixed(1);
  
  // Add currency prefix
  const prefix = includePrefix ? currencyOptions.prefix + ' ' : '';
  
  return `${prefix}${formattedAmount}`;
}

export function formatMoneyWithCurrencyAccounting(amount, currency = '', includePrefix = true, allowDecimals = false) {
  const invalidAmountInputs = [undefined, null, "",];

  if (typeof amount === 'string') {
    amount = Number(amount)
  }

  if (currency === '' && process.env.REACT_APP_COUNTRY === "CHL") currency = 'CLP'
  if (currency === '' && process.env.REACT_APP_COUNTRY === "COL") currency = 'COP'
  
  if (invalidAmountInputs.includes(amount)) return "";

  const currencyOptions = currencyOptionsWithSpecials.find(
    (option) => option.key === currency
  );

  if (!currencyOptions) return "";

  const roundedAmount = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
  const [integers, decimals] = roundedAmount.split(".");

  // Formato de contabilidad internacional: valores negativos entre paréntesis
  const isNegative = amount < 0;
  const absIntegers = Math.abs(amount).toString().split('.')[0];
  
  let formattedAmount;
  
  if (isNegative) {
    // Valor negativo: entre paréntesis
    formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}(${absIntegers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")})`;
  } else {
    // Valor positivo: normal
    formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }

  if (currency !== 'CLP') {
    if (currencyOptions.separation === ',') {
      if (isNegative) {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}(${absIntegers.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${decimals ? `,${decimals}` : ''})`;
      } else {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${decimals ? `,${decimals}` : ''}`;
      }
      return formattedAmount;
    }
    
    if (currencyOptions.separation === '.') {
      if (isNegative) {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}(${absIntegers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''})`;
      } else {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''}`;
      }
      return formattedAmount;
    }
  } else {
    if (allowDecimals) {
      if (isNegative) {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}(${absIntegers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''})`;
      } else {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''}`;
      }
    } else {
      if (isNegative) {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}(${absIntegers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")})`;
      } else {
        formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
      }
    }
  }

  return formattedAmount;
}
export function chartToMoney(amount) {
  return (
    "$ " +
    Math.trunc(amount * 1000000)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  );
}

export const stringToNumber = (value) => {
  if (value === 0) return 0;
  if (!value) return "";
  return value
    .toString()
    .replace(/[^0-9,]/g, "")
    .replace(/\./g, "");
};

export const stringDecimalToNumber = (value) => {
  if (value === 0) return 0;
  if (!value) return 0;

  String(value).replace(/[^0-9,]/g, "");

  const cleanValue = value
    .toString()
    .replace(/[^0-9,.]/g, "")
    .replace(/,/g, ".");

  return parseFloat(cleanValue);
};

export const currency = (value) => {
  if (value === 0) return 0;
  if (!value) return "";
  const number = String(value)
    .replace(/[^0-9,-]/g, "")
    .replace(/\./g, "");
  return String(number).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const parseUsd = (value) => {
  //Cambiamos las comas por un simbolo temporal , luego reemplazamos los , de miles por punto y el simbolo temporal por una coma para el formato
  const stringWithTempChar = value.replace(/,/g, "#");
  const stringWithComma = stringWithTempChar.replace(/\./g, ",");
  const stringWithInvertedCommaAndDot = stringWithComma.replace(/#/g, ".");
  return stringWithInvertedCommaAndDot;
};

export const formatMoney = (value, currencyType = "UF", toTable = false) => {
  if (value === 0) return "0";
  if (!value) return "";

  if (currencyType === "USD") {
    if (toTable) {
      // Solo número + decimales: evita duplicar el prefijo "US" de CurrencyIcon al no usar style:currency.
      const num =
        typeof value === "number"
          ? value
          : Number(
              String(value)
                .replace(/\./g, "")
                .replace(",", ".")
            );
      if (Number.isNaN(num)) return "";
      const formattedResponse = num.toLocaleString("en-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return parseUsd(formattedResponse);
    }

    value = value.toString().replace(/\./g, "");
    const formattedResponse = value.toLocaleString("en-ES", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formattedResponse.replace("$", "");
  }
  const stringAmount = value.toString();
  const amount = stringAmount.replace(/[^.,0-9-]/g, "");
  const commaPosition = amount.indexOf(",");
  if (commaPosition < 0) return currency(amount);
  const intMoney = amount.split(",")[0];
  const decMoney = amount.split(",")[1];
  if (currencyType === "CLP") return currency(intMoney);
  return currency(intMoney + "," + String(decMoney).substring(0, 2));
};

export function formatNewMoney(amount, currencyType = "CLP") {
  if ([undefined, null, ""].includes(String(amount).trim())) return "";
  let [int, dec] = String(amount)
    .replace(/[^0-9-.]|-(?=[^-]*-)|\.(?=[^.]*\.)/g, "")
    .split("."); // removes all non-numeric characters except for the minus sign and the dot
  const absInt =
    int.replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace("-", "") || "0"; // in case of null or undefined, returns 0

  if (dec) {
    dec = "," + dec.slice(0, 2);
  } else {
    dec = "";
  }

  return `${int.includes("-") ? "-" : ""}${absInt + dec}`;
}

export function formatWithDots(amount) {
  const str = String(amount).replace(/[^0-9]/g, "");
  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function cleanNumber(amount) {
  let result = String(amount).replace(/[^0-9-,.]|(?!^)-|\.(?=[^.]*\.)/g, "");
  result = result
    .replace(",", ".")
    .replace(/\.(?=[^.]*\.)/g, "")
    .replace("$", "");

  return Number(result);
}

export const moneyToNumber = (money) => {
  if (!money) return 0;
  if (typeof money == "number") return money;
  return parseFloat(money.replace(/[^0-9,]/g, "").replace(/,/, "."));
};

export const cleanRut = (input) => {
  if (!input) return "";
  return String(input).replace(/[^0-9Kk]/g, "");
};

export const formatRut = (rut) => {
  if (process.env.REACT_APP_COUNTRY === "CHL") {
    if (!rut) return "";
    const actual = rut.replace(/^0+/, "");
    if (actual !== "" && actual.length > 1) {
      const actualClean = actual.replace(/[^0-9Kk]/g, "");
      const start = actualClean.substring(0, actualClean.length - 1);
      let rutDots = "";
      var j = 1;
      for (let i = start.length - 1; i >= 0; i--) {
        const letter = start.charAt(i);
        rutDots = `${letter}${rutDots}`;
        if (j % 3 === 0 && j <= start.length - 1) {
          rutDots = `.${rutDots}`;
        }
        j++;
      }
      const dv = actualClean.substring(actualClean.length - 1);
      rutDots = `${rutDots}-${dv}`;
      return rutDots.toUpperCase();
    }
    return rut;
  } else {
    return formatNitColombia(rut);
  }
};

const formatNitColombia = (nit) => {

  let verificationDigitWeights = [
    3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71,
  ];
  
  if (!nit ||nit === '-') return "";
  
  let nitText = nit.toString();
  
  if (nitText.includes("-")) {
    if (nitText.endsWith('-')) {
      nitText = nitText.slice(0, -2);
    }else{
      nitText = nitText.replace(/-\d/, '')

    }
  }

  const reversedNitArray = nitText.split("").reverse();
  let sum = 0;

  verificationDigitWeights.forEach((element, pos) => {
    if (pos < nitText.length) {
      sum += element * parseInt(reversedNitArray[pos], 10);
    }
  });

  const mod = sum % 11;
  let verificationDigit;

  if (mod === 0) {
    verificationDigit = 0;
  } else if (mod === 1) {
    verificationDigit = 1;
  } else {
    verificationDigit = 11 - mod;
  }

  const formattedNit = nitText.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return formattedNit ? `${formattedNit}-${verificationDigit}` : '';
};

export const formatVat = (vat) => {
  if (!vat) return "";
  const parsedVat = vat.toUpperCase();
  const actual = parsedVat.replace(/[^A-Z0-9]/g, "");
  return actual;
};

export const rutWithNumbers = (rut) => {
  if (!rut) return "";
  return rut.replace(/[^0-9Kk]/g, "");
};

export const rutWithDash = (rut) => {
  if (!rut) return "";
  //if (rut.includes("-")) return rut.toString().replace(/[^0-9Kk-]/g, "");
  const cleanRut = rutWithNumbers(rut.toString());

  const length = cleanRut.length;
  const base = cleanRut.substring(0, length - 1);
  const dv = cleanRut.substring(length - 1);

  return `${base}-${dv}`;
};

export const finalScroll = (value) => {
  if (
    value?.scrollHeight <=
    Math.round(value?.scrollTop + value?.offsetHeight) + 1
  ) {
    return true;
  }
  return false;
};

export const startScroll = (value) => {
  if (value?.scrollTop <= 5) {
    return true;
  }
  return false;
};

export const numbersOnly = (input) => {
  if (!input) return "";
  return input.toString().replace(/[^0-9]/g, "");
};

export const porcentageFormat = (input) => {
  if (!input) return "";
  return input.toString().replace(/[^0-9,]/g, "");
};

const Fn = {
  // Valida el rut con su cadena completa "XXXXXXXX-X"
  validaRut: function (rutCompleto) {
    if (!/^[0-9]+[-|‐]{1}[0-9kK]{1}$/.test(rutCompleto)) return false;
    var tmp = rutCompleto.split("-");
    var digv = tmp[1];
    var rut = tmp[0];
    if (digv === "K") digv = "k";
    // dv() retorna number (0-9) o "k"; digv es string → comparar como string
    return String(Fn.dv(rut)) === String(digv);
  },
  dv: function (T) {
    var M = 0,
      S = 1;
    for (; T; T = Math.floor(T / 10)) S = (S + (T % 10) * (9 - (M++ % 6))) % 11;
    return S ? S - 1 : "k";
  },
};

export const isRutValid = (rutCompleto) => {
  return Fn.validaRut(rutCompleto);
};

export const removeSpaces = (originalString) => {
  if (!originalString) return originalString;

  return originalString.replace(/[\s]/g, "");
};

export const removeAccentMark = (originalString) => {
  if (!originalString) return originalString;

  return originalString.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const isEmailValid = (email) => {
  const emailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isEmail = emailValid.test(email);
  return isEmail;
};

export const formatMoneyWithCurrency = (amount, currency = '', includePrefix = true, allowDecimals = false) => {
  const invalidAmountInputs = [undefined, null, "",];

  if (typeof amount === 'string') {
    amount = Number(amount)
  }

  if (currency === '' && process.env.REACT_APP_COUNTRY === "CHL") currency = 'CLP'
  if (currency === '' && process.env.REACT_APP_COUNTRY === "COL") currency = 'COP'
  
  if (invalidAmountInputs.includes(amount)) return "";

  const currencyOptions = currencyOptionsWithSpecials.find(
    (option) => option.key === currency
  );

  if (!currencyOptions) return "";

  const decimalPlaces = currency !== 'CLP' || allowDecimals
    ? (currencyOptions.decimals ?? 2)
    : 0;
  const roundedAmount = decimalPlaces > 0
    ? Number(amount).toFixed(decimalPlaces)
    : Math.round(Number(amount)).toString();
  const [integers, decimals] = roundedAmount.split(".");

  let formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

  if (currency !== 'CLP') {
    if (currencyOptions.separation === ',') {
      formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${decimals ? `,${decimals}` : ''}`;
      return formattedAmount;
    }
    
    if (currencyOptions.separation === '.') {
      formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ' : ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''}`;
      return formattedAmount;
    }
  } else {
    if (allowDecimals) {
      formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}${decimals ? `,${decimals}` : ''}`;
    } else {
      formattedAmount = `${includePrefix ? currencyOptions.prefix + ' ': ''}${integers.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
    }
  }

  return formattedAmount;
};

function decimalAmountInt(value) {
  return String(value || "").replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
}

function withThousandDots(intPart) {
  return String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** Keeps a decimal amount as text (comma separator) while the user types. */
export function sanitizeDecimalAmount(raw, decimals) {
  if (raw === 0) return "0";
  if (raw == null || raw === "") return "";
  const maxDecimals = Number(decimals) > 0 ? Number(decimals) : 0;
  const value = String(raw).replace(/[^0-9,.]/g, "");
  if (!value) return "";

  const commaIndex = value.lastIndexOf(",");
  if (commaIndex >= 0) {
    const intPart = decimalAmountInt(value.slice(0, commaIndex)) || "0";
    const decPart = value.slice(commaIndex + 1).replace(/[^0-9]/g, "").slice(0, maxDecimals);
    if (decPart) return `${intPart},${decPart}`;
    if (value.endsWith(",")) return `${intPart},`;
    return intPart;
  }

  if (value.includes(".")) {
    const parts = value.split(".");
    const isThousands =
      !value.endsWith(".") &&
      parts[0].length > 0 &&
      parts[0].length <= 3 &&
      parts.slice(1).every((part) => part.length === 3);
    if (isThousands) return decimalAmountInt(value) || "0";

    const intPart = decimalAmountInt(parts.slice(0, -1).join("")) || "0";
    const decPart = parts[parts.length - 1].replace(/[^0-9]/g, "").slice(0, maxDecimals);
    if (value.endsWith(".") && !decPart) return `${intPart},`;
    if (decPart) return `${intPart},${decPart}`;
    return intPart;
  }

  return decimalAmountInt(value) || "0";
}

/** Displays a decimal amount with dot thousands and a comma decimal separator. */
export function formatDecimalAmount(value, decimals) {
  if (value == null || value === "") return "";
  const maxDecimals = Number(decimals) > 0 ? Number(decimals) : 0;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    const [intPart, decPart] = value.toFixed(maxDecimals).split(".");
    return decPart != null ? `${withThousandDots(intPart)},${decPart}` : withThousandDots(intPart);
  }
  const sanitized = sanitizeDecimalAmount(value, maxDecimals);
  if (!sanitized) return "";
  const [intPart, decPart] = sanitized.split(",");
  const withDots = withThousandDots(intPart || "0");
  if (sanitized.endsWith(",") && !decPart) return `${withDots},`;
  if (decPart) return `${withDots},${decPart}`;
  return withDots;
}

/** Parses a CLP integer or a comma/dot decimal amount into a number. */
export function decimalAmountToNumber(value) {
  if (value == null || value === "") return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  let raw = String(value).trim().replace(/[^0-9,.-]/g, "");
  if (!raw) return undefined;
  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    raw = lastComma > lastDot
      ? raw.replace(/\./g, "").replace(",", ".")
      : raw.replace(/,/g, "");
  } else if (lastComma >= 0) {
    raw = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot >= 0) {
    const parts = raw.split(".");
    const isThousands = parts.slice(1).every((part) => part.length === 3);
    raw = isThousands ? parts.join("") : `${parts.slice(0, -1).join("")}.${parts[parts.length - 1]}`;
  }
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}
